/**
 * T3+T4 (Store Improvements Backlog, 2026-08-30). Uploads a customer's
 * design file(s) to the request-service backend (POST /upload -> Shopify
 * Files), then stores the returned CDN URL(s) in a hidden cart line-item
 * property so they travel with the product into the cart normally.
 * Multiple files are joined with newlines into the one property value
 * (2026-09-03: was single-file only).
 * 2026-10-14: one link per property instead ("Dizaina faili", "Dizaina faili 2", ...) -
 * Shopify admin only makes a property clickable when it holds a single URL. The extra
 * hidden inputs are clones of the first one.
 */
class PurchaseRequestUpload extends HTMLElement {
  connectedCallback() {
    this.fileInput = this.querySelector('[ref="fileInput"]');
    this.urlInput = this.querySelector('[ref="designFileUrl"]');
    this.status = this.querySelector('[ref="status"]');
    this.apiBase = (this.dataset.apiBase || '').replace(/\/$/, '');
    this.formId = this.urlInput ? this.urlInput.getAttribute('form') : null;
    this.uploading = false;

    if (this.fileInput) {
      this.fileInput.addEventListener('change', () => this.handleFileChange());
    }

    // Safety net while uploading: the file URLs only land in the hidden
    // property once every upload finishes, so adding to cart earlier would
    // silently drop the design. Blocks the main and sticky add-to-cart
    // buttons (capture phase, before the theme's own handlers) and the form.
    this._blockClick = (event) => {
      if (!this.uploading) return;
      const button = event.target.closest && event.target.closest('button[name="add"], .add-to-cart-button');
      if (!button) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setStatus('Lūdzu, uzgaidiet – faili vēl augšupielādējas.', true);
    };
    this._blockSubmit = (event) => {
      if (!this.uploading || !this.formId || event.target.id !== this.formId) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    document.addEventListener('click', this._blockClick, true);
    document.addEventListener('submit', this._blockSubmit, true);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._blockClick, true);
    document.removeEventListener('submit', this._blockSubmit, true);
  }

  addToCartButtons() {
    return Array.from(document.querySelectorAll('button[name="add"], .add-to-cart-button'));
  }

  setUploading(uploading) {
    this.uploading = uploading;
    for (const button of this.addToCartButtons()) {
      if (uploading) {
        button.dataset.uploadWasDisabled = button.disabled ? 'true' : 'false';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.style.opacity = '0.5';
        button.style.cursor = 'wait';
      } else if (button.dataset.uploadWasDisabled !== undefined) {
        // Restore whatever state the theme had (e.g. sold-out stays disabled).
        button.disabled = button.dataset.uploadWasDisabled === 'true';
        delete button.dataset.uploadWasDisabled;
        button.removeAttribute('aria-busy');
        button.style.opacity = '';
        button.style.cursor = '';
      }
    }
  }

  async handleFileChange() {
    const files = Array.from(this.fileInput.files || []);
    // Clear the previous selection's URLs right away, so a replaced file can
    // never be added to the cart in place of the new one.
    this.setUrls([]);
    if (!files.length) {
      this.setStatus('', false);
      return;
    }

    if (!this.apiBase) {
      this.setStatus('Faili netiks nosūtīti – sistēma vēl nav pieslēgta.', true);
      return;
    }

    this.setStatus(`Augšupielādē failus (${files.length})… Lūdzu, uzgaidiet, pirms pievienojat grozam.`, false);
    this.setUploading(true);

    try {
      const urls = await Promise.all(
        files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          const response = await fetch(`${this.apiBase}/upload`, {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            throw new Error(`Upload failed with status ${response.status}`);
          }

          const data = await response.json();
          // 2026-10-14: direct-download link (original file, not the CDN's WebP); the request service unwraps it for the email/ZIP
          return data.download_url || data.url;
        })
      );

      this.setUrls(urls);
      this.setStatus(`Pievienoti ${urls.length} faili: ${files.map((f) => f.name).join(', ')}`, false);
    } catch (error) {
      this.setUrls([]);
      this.setStatus('Neizdevās augšupielādēt vienu vai vairākus failus, mēģiniet vēlreiz.', true);
    } finally {
      this.setUploading(false);
    }
  }

  setUrls(urls) {
    if (!this.urlInput) return;
    this.querySelectorAll('[data-extra-design-file]').forEach((el) => el.remove());
    this.urlInput.value = urls[0] || '';
    let last = this.urlInput;
    urls.slice(1).forEach((url, i) => {
      const extra = this.urlInput.cloneNode(false);
      extra.removeAttribute('ref');
      extra.setAttribute('data-extra-design-file', '');
      extra.name = `properties[Dizaina faili ${i + 2}]`;
      extra.value = url;
      last.after(extra);
      last = extra;
    });
  }

  setStatus(text, isError) {
    if (!this.status) return;
    this.status.textContent = text;
    this.status.classList.toggle('purchase-request-designer__status--error', Boolean(isError));
  }
}

customElements.define('purchase-request-upload', PurchaseRequestUpload);
